import { forwardRef } from 'react';

import { Button as BaseButton } from '@base-ui/react/button';
import { cn } from 'components/utils';

import { buttonRecipe } from './Button.recipe';
import { ButtonContent } from './ButtonContent';

import type { IconButtonProps } from './types';

/**
 * Design System 2.0 icon-only button.
 *
 * A named front door for `Button`'s `iconOnly` axis, NOT a second implementation. It renders the
 * same `buttonRecipe` and the same `ButtonContent`, so the slot reads, disabled treatment, focus
 * ring and size scale cannot drift from the text button's. What it adds is a smaller, honest API:
 * `aria-label` is required, and `fullWidth` / `startIcon` / `endIcon` are gone because a square
 * button cannot use them.
 *
 * Circular by construction rather than by a radius override: the recipe's base list carries
 * `rounded-full`, and `iconOnly` makes the box square (24 / 32 / 36 / 40 / 44 by size), so a fully
 * rounded rectangle resolves to a true circle at every size.
 *
 * The 24px cell (`size="2xs"`) is reachable here and nowhere else — `Button` and `ButtonLink` take
 * `ButtonSize`, which does not include it. See `IconButtonSize` in `types.ts`.
 *
 * Built on Base UI's `Button` for button semantics and keyboard activation. For navigation, use
 * `ButtonLink` — an icon-only link needs an accessible name too, so pass `aria-label` there as well.
 */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ appearance, fill, size, loading, disabled, className, children, ...rest }, ref) => (
    <BaseButton
      ref={ref}
      disabled={disabled || loading}
      focusableWhenDisabled={loading || undefined}
      aria-busy={loading || undefined}
      className={cn(buttonRecipe({ appearance, fill, size, iconOnly: true, loading }), className)}
      {...rest}
    >
      <ButtonContent iconOnly loading={loading}>
        {children}
      </ButtonContent>
    </BaseButton>
  )
);
